/* ÜRETİLMİŞ DOSYA — ELLE DÜZENLEMEYİN.
   Kaynak: ris-api/RIS.UI/src/shared/template-builder
   Güncelleme: ris-api/scripts/sync-template-builder.sh */

import { Badge, Button, Col, Drawer, Row, Segmented, Tooltip, message } from 'antd'
import {
    CheckCircleOutlined,
    EyeOutlined,
    SaveOutlined,
    SettingOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { useCallback, useMemo, useRef, useState } from 'react'
import BlockList from './components/BlockList'
import HeaderFooterEditor from './components/HeaderFooterEditor'
import PageSettings from './components/PageSettings'
import PlaceholderPalette from './components/PlaceholderPalette'
import PreviewPane from './components/PreviewPane'
import ValidationPanel from './components/ValidationPanel'
import { collectTokens, emptyDefinition, missingTranslationCount } from './model'
import { writeClipboard } from '../../core/utils/clipboard-utils'
import './styles.css'

/**
 * Şablon builder — RIS ve HIS'in ortak kullandığı bileşen.
 *
 * Paket uygulamaya özgü hiçbir şey bilmez: alan katalogu, dil listesi ve API çağrıları prop olarak gelir.
 * Böylece aynı kod hem hastane rapor çıktı şablonlarında hem birim onam formlarında çalışır.
 *
 * TASARIM KARARI: içerik yazmak ana eylem, geri kalanı çevre. Ekranda yalnız **blok editörü** ve
 * **alan paleti** durur; sayfa/yazı tipi/filigran/dil ayarları, PDF önizleme ve denetim birer çekmeceye
 * alınmıştır. Önceki düzende üç kolon + iki seviye sekme vardı ve asıl çalışma alanına ekranın ~%37'si
 * kalıyordu — şimdi %75.
 *
 * Props:
 *   value / onChange       — şablon tanımı (TemplateDefinition)
 *   placeholders           — [{token,label,group,kind,sample,note}]
 *   languages              — seçilebilir dil kodları
 *   defaultLanguage        — varsayılan dil
 *   onPreview(def, lang)   — Promise<{pdfFile, warnings}>
 *   onSave(def)            — Promise
 *   editorApiKey           — TinyMCE anahtarı (host uygulamanın kendi anahtarı)
 *   t                      — çeviri fonksiyonu
 */
export default function TemplateBuilder({
    value,
    onChange,
    placeholders = [],
    languages = ['tr'],
    defaultLanguage = 'tr',
    onPreview,
    onSave,
    editorApiKey = '',
    readOnly = false,
    t = (x) => x,
}) {
    const definition = value || emptyDefinition(languages, defaultLanguage)

    const [activeLanguage, setActiveLanguage] = useState(definition.defaultLanguage || languages[0] || 'tr')
    const [region, setRegion] = useState('body')
    const [saving, setSaving] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [issuesOpen, setIssuesOpen] = useState(false)
    const insertRef = useRef(null)

    const registerInsert = useCallback((fn) => { insertRef.current = fn }, [])

    const update = (next) => { if (!readOnly) onChange(next) }

    const insertToken = (token) => {
        if (insertRef.current) {
            insertRef.current(token)
            return
        }
        // Aktif bir metin editörü yoksa (blok katlı ya da tablo düzenleniyor) panoya kopyalanır.
        // Güvenli olmayan bağlamda Clipboard API yok; writeClipboard execCommand'a düşer.
        writeClipboard(token).then(ok => {
            if (ok) message.info(t('Alan panoya kopyalandı: ') + token)
            else message.error(t('Copy failed'))
        })
    }

    const save = async () => {
        if (!onSave) return
        setSaving(true)
        try {
            await onSave(definition)
        } finally {
            setSaving(false)
        }
    }

    // Denetim sayacı araç çubuğunda rozet olarak durur; ayrıntı çekmecede. Doğrulamanın sürekli
    // ekranda bir kart tutması gerekmiyor — o bir durum göstergesi, çalışma alanı değil.
    const issueCount = useMemo(() => {
        const known = new Set(
            placeholders.map((p) => String(p.token || '').replace(/\s/g, '').toLowerCase())
        )
        const unknown = collectTokens(definition).filter(
            (token) => !known.has(String(token).replace(/\s/g, '').toLowerCase())
        ).length
        const missing = (definition.languages || []).reduce(
            (sum, lang) => sum + missingTranslationCount(definition, lang),
            0
        )
        return unknown + missing
    }, [definition, placeholders])

    // Dil, sekme değil **görünüm anahtarı**: aynı belgeye başka dilden bakılıyor.
    const languageOptions = useMemo(
        () =>
            (definition.languages || []).map((lang) => {
                const missing = missingTranslationCount(definition, lang)
                return {
                    value: lang,
                    label: (
                        <Badge count={missing} size="small" offset={[7, -3]}>
                            <span style={{ padding: '0 2px' }}>{lang.toUpperCase()}</span>
                        </Badge>
                    ),
                }
            }),
        [definition]
    )

    const regionEditor = () => {
        if (region === 'body')
            return (
                <BlockList
                    blocks={definition.body || []}
                    language={activeLanguage}
                    languages={definition.languages}
                    placeholders={placeholders}
                    apiKey={editorApiKey}
                    registerInsert={registerInsert}
                    t={t}
                    onChange={(body) => update({ ...definition, body })}
                />
            )

        const isFooter = region === 'footer'
        return (
            <HeaderFooterEditor
                isFooter={isFooter}
                area={isFooter ? definition.footer : definition.header}
                onChange={(area) => update(isFooter ? { ...definition, footer: area } : { ...definition, header: area })}
                language={activeLanguage}
                languages={definition.languages}
                placeholders={placeholders}
                apiKey={editorApiKey}
                registerInsert={registerInsert}
                t={t}
            />
        )
    }

    return (
        <div className="tb-root">
            <div className="tb-bar">
                <Segmented
                    size="small"
                    value={region}
                    onChange={setRegion}
                    options={[
                        { value: 'body', label: t('Gövde') },
                        { value: 'header', label: t('Üstbilgi') },
                        { value: 'footer', label: t('Altbilgi') },
                    ]}
                />

                <span className="tb-bar-spacer" />

                {(definition.languages || []).length > 1 && (
                    <Segmented
                        size="small"
                        value={activeLanguage}
                        onChange={setActiveLanguage}
                        options={languageOptions}
                    />
                )}

                <Tooltip title={t('Sayfa, yazı tipi, filigran, diller')}>
                    <Button size="small" icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
                        {t('Ayarlar')}
                    </Button>
                </Tooltip>

                <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)} disabled={!onPreview}>
                    {t('Önizleme')}
                </Button>

                <Badge count={issueCount} size="small">
                    <Tooltip title={issueCount ? t('Dikkat edilmesi gerekenler') : t('Sorun görünmüyor')}>
                        <Button
                            size="small"
                            icon={issueCount ? <WarningOutlined /> : <CheckCircleOutlined />}
                            onClick={() => setIssuesOpen(true)}
                        >
                            {t('Denetim')}
                        </Button>
                    </Tooltip>
                </Badge>

                <Tooltip title={t('Kaydedince şablon builder motoruna (v2) geçer')}>
                    <Button
                        type="primary"
                        size="small"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={save}
                        disabled={readOnly || !onSave}
                    >
                        {t('Kaydet')}
                    </Button>
                </Tooltip>
            </div>

            <Row gutter={12} className="tb-body">
                <Col xs={24} lg={18}>{regionEditor()}</Col>

                <Col xs={24} lg={6}>
                    <div className="tb-palette-card">
                        <div className="tb-palette-title">{t('Alanlar')}</div>
                        <PlaceholderPalette placeholders={placeholders} onInsert={insertToken} t={t} />
                    </div>
                </Col>
            </Row>

            <Drawer
                title={t('Şablon Ayarları')}
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                width={720}
            >
                <PageSettings
                    value={definition}
                    onChange={update}
                    language={activeLanguage}
                    selectableLanguages={languages}
                    onActiveLanguageChange={setActiveLanguage}
                    t={t}
                />
            </Drawer>

            <Drawer
                title={t('Önizleme')}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                width="72vw"
            >
                <PreviewPane
                    onPreview={onPreview}
                    definition={definition}
                    language={activeLanguage}
                    t={t}
                />
            </Drawer>

            <Drawer
                title={t('Denetim')}
                open={issuesOpen}
                onClose={() => setIssuesOpen(false)}
                width={480}
            >
                <ValidationPanel definition={definition} placeholders={placeholders} t={t} />
            </Drawer>
        </div>
    )
}